export interface EntitlementSignal {
  hasLiveEntitlements: boolean
  planCount: number
  featureNames: string[]
}

// `planEntitlements` is generated as `any` by the SDK -- its real shape
// (year -> audience -> flat array | quarter-keyed object) isn't modeled
// there. Walk it defensively with `Object.values` at every level rather than
// hardcoding a fixed audience list, so a newly added audience (e.g.
// `large_group_custom`, added after this endpoint was first documented)
// still counts correctly.
function countPlanEntitlements(planEntitlements: unknown): number {
  if (planEntitlements == null || typeof planEntitlements !== 'object') return 0

  let count = 0
  for (const yearValue of Object.values(planEntitlements as Record<string, unknown>)) {
    if (yearValue == null || typeof yearValue !== 'object') continue

    for (const audienceValue of Object.values(yearValue as Record<string, unknown>)) {
      if (Array.isArray(audienceValue)) {
        count += audienceValue.length
      } else if (audienceValue != null && typeof audienceValue === 'object') {
        for (const quarterValue of Object.values(audienceValue as Record<string, unknown>)) {
          if (Array.isArray(quarterValue)) count += quarterValue.length
        }
      }
    }
  }

  return count
}

// IMPORTANT: `hasLiveEntitlements` is derived ONLY from plan and feature
// entitlements. `carrierEntitlements`/`networkEntitlements` merge in
// wildcard (account_id: NULL) rows at read time, so they read as
// non-empty/non-null for EVERY account once a single wildcard row exists
// anywhere in the system -- they are not valid per-account evidence and
// must never feed this signal (see docs/knowledge/subsystems/service-objects.md, docs/knowledge/known-issues-entitlements.md).
export function summarizeEntitlementSignal(entitlements: {
  planEntitlements?: unknown
  featureEntitlements: Array<{ feature: string; value?: unknown }>
}): EntitlementSignal {
  const planCount = countPlanEntitlements(entitlements.planEntitlements)
  const featureNames = entitlements.featureEntitlements.map((entry) => entry.feature)

  return {
    hasLiveEntitlements: planCount > 0 || featureNames.length > 0,
    planCount,
    featureNames,
  }
}
